import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiError } from "@/lib/campus";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Shell = ({ title, children }) => (
  <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4">
    <div className="w-full max-w-md">
      <Link to="/" className="flex justify-center mb-8"><Logo size={40} /></Link>
      <div className="rounded-2xl bg-[#121721] border border-white/10 p-8 space-y-5">
        <h1 className="text-2xl font-bold text-white text-center">{title}</h1>
        {children}
      </div>
    </div>
  </div>
);
const inputCls = "bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500";
const btnCls = "w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold h-11 rounded-full";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err, "Couldn't send the link"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Reset your password">
      {sent ? (
        <p className="text-sm text-slate-300 text-center" data-testid="forgot-sent">If that email has a password account, a reset link is on its way. Check your inbox (and spam).</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label className="text-slate-300">Email</Label>
            <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></div>
          <Button data-testid="forgot-submit" disabled={busy} className={btnCls}>{busy ? "Sending…" : "Email me a reset link"}</Button>
        </form>
      )}
      <p className="text-center text-sm"><Link to="/login" data-testid="forgot-back" className="text-amber-400 hover:text-amber-300">Back to sign in</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", new_password: pw });
      toast.success("Password updated — sign in with your new password");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err, "Couldn't reset password"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2"><Label className="text-slate-300">New password (8+ characters)</Label>
          <Input data-testid="reset-password" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} /></div>
        <div className="space-y-2"><Label className="text-slate-300">Confirm password</Label>
          <Input data-testid="reset-confirm" type="password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} /></div>
        <Button data-testid="reset-submit" disabled={busy} className={btnCls}>{busy ? "Saving…" : "Update password"}</Button>
      </form>
    </Shell>
  );
}
